import { Ref, ref, ComputedRef, computed } from 'vue'
import { useLoading, Loading } from '@/composable/useLoading'
import { createTrackCount } from '@/api/track-count/createTrackCount'
import { updateTrackCount } from '@/api/track-count/updateTrackCount'
import { deleteTrackCount } from '@/api/track-count/deleteTrackCount'
import { v4 as uuidv4 } from 'uuid'
import { getTrackCount, TrackCount } from '@/api/track-count/getTrackCount'

interface UseTrackCount {
  data: Ref<TrackCount[]>
  fetchAll: () => Promise<TrackCount[]>
  add: (body: TrackCount[]) => Promise<TrackCount[]>
  remove: (uuid: string) => Promise<null>
  update: (body: TrackCount[]) => Promise<TrackCount[]>
  loading: ComputedRef<Loading>
}

const data = ref<TrackCount[]>([])

export function useTrackCount(initFetchAll?: boolean): UseTrackCount {
  const { state } = useLoading()

  async function fetchAll() {
    state.getAll = true
    return new Promise<TrackCount[]>((resolve, reject) => {
      getTrackCount()
        .then((trackCount) => {
          if (trackCount) {
            data.value = trackCount
          }
          resolve(trackCount)
        })
        .catch(reject)
        .finally(() => {
          state.getAll = false
        })
    })
  }

  async function add(body: TrackCount[] | TrackCount) {
    state.create = true
    return new Promise<TrackCount[]>((resolve, reject) => {
      const payload = Array.isArray(body) ? body : [body]
      return createTrackCount(payload)
        .then(() => {
          data.value = [
            ...data.value.filter((x) =>
              payload.some((p) => p.from !== x.from && p.to !== x.to)
            ),
            ...payload.map((x) => ({ ...x, uuid: uuidv4() })),
          ]
          resolve(payload)
        })
        .catch(reject)
        .finally(() => {
          state.create = false
        })
    })
  }

  async function update(body: TrackCount[]) {
    state.update = true
    return new Promise<TrackCount[]>((resolve, reject) => {
      const payload = Array.isArray(body) ? body : [body]
      return updateTrackCount(payload)
        .then(() => {
          data.value = [
            ...data.value.filter((x) => payload.some((p) => p.uuid !== x.uuid)),
            ...payload.map((x) => ({ ...x })),
          ]
          resolve(body)
        })
        .catch(reject)
        .finally(() => {
          state.update = false
        })
    })
  }

  async function remove(uuid: string) {
    state.delete = true
    const trackCount = data.value.find((x) => x.uuid === uuid)
    if (!trackCount) return Promise.resolve(null)

    return new Promise<null>((resolve, reject) => {
      deleteTrackCount(trackCount)
        .then(() => {
          data.value = data.value.filter(
            (item) => trackCount.from !== item.from && trackCount.to !== item.to
          )
          resolve(null)
        })
        .catch(reject)
        .finally(() => {
          state.delete = false
        })
    })
  }

  if (initFetchAll) {
    fetchAll()
  }

  return {
    data,
    fetchAll,
    add,
    remove,
    update,
    loading: computed(() => state),
  }
}
